"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PAGE_TREE_UNPLACED_ID = exports.PAGE_TREE_DELETED_ID = exports.PAGE_TREE_RECOVERED_ID = void 0;
exports.isPageTreeEntry = isPageTreeEntry;
exports.derivePageTree = derivePageTree;
exports.pageTreeChildIds = pageTreeChildIds;
exports.pageTreeElementIds = pageTreeElementIds;
exports.pageTreeElementLists = pageTreeElementLists;
exports.pageTreeMemberLists = pageTreeMemberLists;
exports.pageTreeChildLists = pageTreeChildLists;
const page_structure_entry_1 = require("./page-structure-entry");
/**
 * Id of the virtual node „Wiederhergestellt“ at the end of the page: it holds
 * every entry whose parent is unknown, the cut entry of each cycle and every
 * root beyond the first. It is never stored; `$` is no part of a block id.
 */
exports.PAGE_TREE_RECOVERED_ID = '$recovered';
/** Parent of stored entries marked deleted and absent from the tree. */
exports.PAGE_TREE_DELETED_ID = '$deleted';
/**
 * Parent of a live block that belongs to the page without a place in its
 * tree, like a block of the old format in no child list (a hidden App
 * table): read by its id, never shown, kept by the prune.
 */
exports.PAGE_TREE_UNPLACED_ID = '$unplaced';
/** Whether the entry has a place in the page tree: neither deleted nor unplaced. */
function isPageTreeEntry(entry) {
    return entry.p !== exports.PAGE_TREE_DELETED_ID && entry.p !== exports.PAGE_TREE_UNPLACED_ID;
}
// Code unit order: fractional indexes are ASCII, locale order would differ
// between devices.
const compare = (a, b) => (a < b ? -1 : a > b ? 1 : 0);
/**
 * Derives the page tree from its structure entries: children by parent id
 * sorted by (o, id). An unknown parent puts the entry under the recovered
 * node, a cycle is cut at its smallest id, which also goes there. Group and
 * frame membership (g) is repaired the same way: an unknown group drops the
 * membership, a cycle of groups is cut at its smallest id. Canvas elements
 * stand apart from the blocks, in the layer order of their surface. The
 * result depends on the entries only, never on their order or the device.
 */
function derivePageTree(source, hidden, unloaded) {
    const stored = new Map(source);
    // Descendants of tombstones stay hidden until their parent is restored.
    const entries = new Map([...stored].filter(([id, entry]) => isPageTreeEntry(entry) && !hidden?.(id, entry)));
    {
        const children = new Map();
        const pending = [];
        for (const [id, entry] of entries) {
            if (entry.p === null)
                continue;
            const siblings = children.get(entry.p) ?? [];
            siblings.push(id);
            children.set(entry.p, siblings);
            if (stored.get(entry.p)?.p === exports.PAGE_TREE_DELETED_ID ||
                (!entries.has(entry.p) && unloaded?.(entry.p)))
                pending.push(id);
        }
        const omitted = new Set();
        for (const id of pending) {
            if (omitted.has(id))
                continue;
            omitted.add(id);
            pending.push(...(children.get(id) ?? []));
        }
        for (const id of omitted)
            entries.delete(id);
    }
    const byOrder = (a, b) => compare(entries.get(a).o, entries.get(b).o) || compare(a, b);
    const ids = [...entries.keys()].sort(compare);
    const isElement = (id) => (0, page_structure_entry_1.isPageElementKind)(entries.get(id).k);
    const blockIds = ids.filter(id => !isElement(id));
    const tops = blockIds.filter(id => entries.get(id).p === null).sort(byOrder);
    const root = tops[0] ?? null;
    const parentOf = new Map();
    for (const id of blockIds) {
        const parent = entries.get(id).p;
        if (parent === null) {
            if (id !== root)
                parentOf.set(id, exports.PAGE_TREE_RECOVERED_ID);
        }
        else {
            parentOf.set(id, entries.has(parent) && parent !== id && !isElement(parent)
                ? parent
                : exports.PAGE_TREE_RECOVERED_ID);
        }
    }
    cutCycles(blockIds, parentOf, id => id === root, exports.PAGE_TREE_RECOVERED_ID);
    const children = new Map();
    for (const [id, parent] of parentOf) {
        const list = children.get(parent);
        if (list)
            list.push(id);
        else
            children.set(parent, [id]);
    }
    for (const list of children.values())
        list.sort(byOrder);
    const recovered = children.get(exports.PAGE_TREE_RECOVERED_ID) ?? [];
    if (root !== null && recovered.length > 0) {
        const rootChildren = children.get(root) ?? [];
        rootChildren.push(exports.PAGE_TREE_RECOVERED_ID);
        children.set(root, rootChildren);
    }
    // A canvas element stands on its block; one whose block is unknown goes
    // to the first surface of the page.
    const surfaces = blockIds
        .filter(id => entries.get(id).k === page_structure_entry_1.PAGE_SURFACE_FLAVOUR)
        .sort(byOrder);
    const elements = new Map();
    for (const id of ids.filter(isElement)) {
        const parent = entries.get(id).p;
        const surface = parent !== null && entries.has(parent) && !isElement(parent)
            ? parent
            : (surfaces[0] ?? exports.PAGE_TREE_RECOVERED_ID);
        const list = elements.get(surface);
        if (list)
            list.push(id);
        else
            elements.set(surface, [id]);
    }
    for (const list of elements.values())
        list.sort(byOrder);
    const groupOf = new Map();
    for (const id of ids) {
        const group = entries.get(id).g;
        if (group !== undefined && group !== id && entries.has(group)) {
            groupOf.set(id, group);
        }
    }
    cutCycles(ids, groupOf, () => false, null);
    const members = new Map();
    for (const [id, group] of groupOf) {
        const list = members.get(group);
        if (list)
            list.push(id);
        else
            members.set(group, [id]);
    }
    for (const list of members.values())
        list.sort(byOrder);
    const presentation = ids
        .filter(id => entries.get(id).pi !== undefined)
        .sort((a, b) => compare(entries.get(a).pi, entries.get(b).pi) || compare(a, b));
    return {
        root,
        children,
        parentOf,
        recovered,
        elements,
        groupOf,
        members,
        presentation,
    };
}
/**
 * Children of a block in reading order. The recovered node holds no fields,
 * so at the root its entries take its place.
 */
function pageTreeChildIds(tree, blockId) {
    return (tree.children.get(blockId) ?? []).flatMap(id => id === exports.PAGE_TREE_RECOVERED_ID ? [...tree.recovered] : [id]);
}
/** Canvas elements of a surface block in layer order. */
function pageTreeElementIds(tree, surfaceId) {
    return tree.elements.get(surfaceId) ?? [];
}
/** Every surface's canvas elements in layer order, as plain data. */
function pageTreeElementLists(tree) {
    return Object.fromEntries([...tree.elements].map(([surfaceId, ids]) => [surfaceId, [...ids]]));
}
/** Every group's and frame's members, as plain data. */
function pageTreeMemberLists(tree) {
    return Object.fromEntries([...tree.members].map(([groupId, ids]) => [groupId, [...ids]]));
}
/** Every block's children in reading order, as plain data for JSON readers. */
function pageTreeChildLists(tree) {
    const lists = {};
    for (const blockId of tree.children.keys()) {
        if (blockId !== exports.PAGE_TREE_RECOVERED_ID) {
            lists[blockId] = pageTreeChildIds(tree, blockId);
        }
    }
    return lists;
}
/**
 * Cuts every cycle of `link` at its smallest id: that id links to `cutTo`
 * instead, or loses its link when `cutTo` is null. `isEnd` marks ids whose
 * chain ends without a link (the root). Walks in id order, so the cuts are
 * the same on every device.
 */
function cutCycles(ids, link, isEnd, cutTo) {
    // 1: on the walk in progress, 2: its chain ends.
    const state = new Map();
    for (const start of ids) {
        if (state.has(start))
            continue;
        const path = [];
        let id = start;
        while (id !== undefined && !state.has(id) && !isEnd(id)) {
            state.set(id, 1);
            path.push(id);
            const next = link.get(id);
            id = next === undefined || next === cutTo ? undefined : next;
        }
        if (id !== undefined && state.get(id) === 1) {
            const cycle = path.slice(path.indexOf(id));
            const cut = cycle.reduce((a, b) => (compare(a, b) <= 0 ? a : b));
            if (cutTo === null)
                link.delete(cut);
            else
                link.set(cut, cutTo);
        }
        for (const visited of path)
            state.set(visited, 2);
    }
}
